<script setup lang="ts">
import { computed, ref, watch } from 'vue';

type Props = {
  /** Dados de estatísticas. null = ainda carregando (renderiza skeletons). */
  statistics?: GameStatisticsByPeriod | null;
  isLoading?: boolean;
  title?: string;
};

const {
  statistics = null,
  isLoading = false,
  title = 'Estatísticas',
} = defineProps<Props>();

// Skeleton enquanto o pai sinaliza loading OU enquanto os dados ainda não
// chegaram. Sem o `!statistics`, há um frame onde `isLoading=false` mas
// `statistics=null` → cards renderizam com '-' / valores zerados antes dos
// reais aparecerem.
const showSkeleton = computed(() => isLoading || !statistics);

const { showMoneyFormat } = useWallet();

type StatPeriod =
  | 'last_24_hours'
  | 'last_7_days'
  | 'last_15_days'
  | 'last_30_days';

const activePeriod = ref<StatPeriod>('last_24_hours');

const periodTabs: { key: StatPeriod; label: string }[] = [
  { key: 'last_24_hours', label: 'HOJE' },
  { key: 'last_7_days', label: '7D' },
  { key: 'last_15_days', label: '15D' },
  { key: 'last_30_days', label: '30D' },
];

const currentPagou = computed(() => {
  if (!statistics) return null;

  const map: Record<StatPeriod, number> = {
    last_24_hours: statistics.wins_last_24_hours,
    last_7_days: statistics.wins_last_7_days,
    last_15_days: statistics.wins_last_15_days,
    last_30_days: statistics.wins_last_30_days,
  };

  return map[activePeriod.value];
});

const currentRtp = computed(() => {
  if (!statistics) return null;

  const map: Record<StatPeriod, number> = {
    last_24_hours: statistics.rtp_last_24_hours,
    last_7_days: statistics.rtp_last_7_days,
    last_15_days: statistics.rtp_last_15_days,
    last_30_days: statistics.rtp_last_30_days,
  };

  return map[activePeriod.value];
});

const currentPlayers = computed(() => {
  if (!statistics) return null;

  const map: Record<StatPeriod, number> = {
    last_24_hours: statistics.players_last_24_hours,
    last_7_days: statistics.players_last_7_days,
    last_15_days: statistics.players_last_15_days,
    last_30_days: statistics.players_last_30_days,
  };

  return map[activePeriod.value];
});

// "Online agora" usa sempre players_last_1_hour, independente da tab de período.
const playersOnline = computed(() => statistics?.players_last_1_hour);

const cards = computed(() => [
  {
    key: 'pagou',
    icon: 'statistics/pagou',
    label: 'Pagou',
    value:
      currentPagou.value != null
        ? showMoneyFormat({ value: currentPagou.value })
        : '-',
  },
  {
    key: 'rtp',
    icon: 'statistics/rtp',
    label: 'RTP',
    value: currentRtp.value != null ? `${Math.round(currentRtp.value)}%` : '-',
  },
  {
    key: 'players',
    icon: 'statistics/jogadores',
    label: 'Players',
    value:
      currentPlayers.value != null
        ? currentPlayers.value.toLocaleString('pt-BR')
        : '-',
  },
  {
    key: 'online',
    icon: 'statistics/online',
    label: 'Online',
    value:
      playersOnline.value != null
        ? playersOnline.value.toLocaleString('pt-BR')
        : '-',
    showLiveDot: true,
  },
]);

// Reseta a tab quando o jogo muda (statistics troca de identidade)
watch(
  () => statistics,
  () => {
    activePeriod.value = 'last_24_hours';
  },
);
</script>

<template>
  <section class="stats-carousel">
    <div class="stats-carousel__header">
      <h3 class="stats-carousel__title">{{ title }}</h3>
      <div class="stats-carousel__period-tabs">
        <button
          v-for="tab in periodTabs"
          :key="tab.key"
          class="stats-carousel__period-btn"
          :class="{ 'is-active': activePeriod === tab.key }"
          type="button"
          @click="activePeriod = tab.key"
        >
          {{ tab.label }}
        </button>
      </div>
    </div>

    <div
      v-if="showSkeleton"
      class="stats-carousel__scroll"
    >
      <div
        v-for="i in 4"
        :key="i"
        class="stats-carousel__card stats-carousel__card--skeleton skeleton-loading"
      />
    </div>
    <div
      v-else
      class="stats-carousel__scroll"
    >
      <div
        v-for="card in cards"
        :key="card.key"
        class="stats-carousel__card"
      >
        <span class="stats-carousel__label">{{ card.label }}</span>
        <span class="stats-carousel__value">
          <span
            v-if="card.showLiveDot"
            aria-hidden="true"
            class="stats-carousel__live-dot"
          />
          {{ card.value }}
        </span>
      </div>
    </div>
  </section>
</template>

<style scoped>
.stats-carousel {
  padding: 12px 0 0;
}

.stats-carousel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.stats-carousel__title {
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  margin: 0;
}

.stats-carousel__period-tabs {
  display: flex;
  gap: 4px;
}

.stats-carousel__period-btn {
  padding: 3px 7px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.4);
  background: transparent;
  border: none;
  cursor: pointer;
  transition:
    color 0.18s ease,
    background 0.18s ease;
}

.stats-carousel__period-btn:hover {
  color: rgba(255, 255, 255, 0.7);
}

.stats-carousel__period-btn.is-active {
  color: #00c9ff;
  background: rgba(0, 201, 255, 0.12);
}

/* Carousel horizontal — mesmo padrão dos chips de tag */
.stats-carousel__scroll {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  padding: 2px 16px 0;
  margin: 0 -16px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  touch-action: pan-x;
  overscroll-behavior-x: contain;
}

.stats-carousel__scroll::-webkit-scrollbar {
  display: none;
}

.stats-carousel__card {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 5px 10px;
  border-radius: 6px;
  background: rgba(0, 201, 255, 0.08);
  white-space: nowrap;
  line-height: 1;
}

.stats-carousel__card--skeleton {
  min-width: 110px;
  height: 22px;
}

.stats-carousel__label {
  color: #00c9ff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}

.stats-carousel__value {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}

.stats-carousel__live-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #00ff00;
  animation: blink 1800ms linear infinite;
}

.skeleton-loading {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.06) 0%,
    rgba(255, 255, 255, 0.12) 50%,
    rgba(255, 255, 255, 0.06) 100%
  );
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s ease-in-out infinite;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
